import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Zap, Cpu, Wrench, Layers, Box, Rocket } from "lucide-react";

const cards = [
  { icon: Cpu, title: "PCB Design", desc: "Multilayer board layout, schematic capture, and DFM-ready files engineered for reliable, manufacturable hardware." },
  { icon: Zap, title: "Firmware", desc: "Bare-metal and RTOS firmware, driver development, and power-efficient embedded logic tailored to your silicon." },
  { icon: Wrench, title: "Industrial Design", desc: "Enclosure design, ergonomics, and material selection that balance aesthetics with real-world durability." },
  { icon: Box, title: "3D Printing", desc: "Rapid prototyping and functional parts with fast iteration cycles from concept to physical model." },
  { icon: Layers, title: "IoT Integration", desc: "Connected device architecture, cloud telemetry, and secure OTA updates across your product fleet." },
  { icon: Rocket, title: "Production", desc: "Scaling from prototype to volume manufacturing with sourcing, testing, and quality assurance built in." },
];

// how long a card stays flipped open before auto-closing and moving to the next one
const OPEN_MS = 1800;
// pause after a card closes before the next one opens
const GAP_MS = 500;

function FlipCard({ icon: Icon, title, desc, flipped, onClick }) {
  return (
    <motion.div
      className="h-72 cursor-pointer"
      style={{ perspective: 1200 }}
      onClick={onClick}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div
        style={{ transformStyle: "preserve-3d", width: "100%", height: "100%", position: "relative" }}
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.6, ease: "easeInOut" }}
      >
        {/* FRONT */}
        <div
          style={{ backfaceVisibility: "hidden", position: "absolute", inset: 0 }}
          className="bg-gray-50 rounded-3xl p-8 flex flex-col items-center justify-center gap-5 border border-gray-100 shadow-sm"
        >
          <div className="w-16 h-16 rounded-2xl bg-[#c021d6] flex items-center justify-center">
            <Icon className="text-white" size={30} />
          </div>
          <h3 className="text-xl font-bold text-[#1a1a1a] text-center">{title}</h3>
          <span className="text-xs font-medium text-[#c021d6] uppercase tracking-widest">
            Click to learn more
          </span>
        </div>

        {/* BACK */}
        <div
          style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)", position: "absolute", inset: 0 }}
          className="bg-gradient-to-br from-fuchsia-600 to-purple-800 rounded-3xl p-8 flex flex-col items-center justify-center gap-4 text-center shadow-lg"
        >
          <h3 className="text-lg font-bold text-white">{title}</h3>
          <p className="text-sm text-white/90 leading-relaxed">{desc}</p>
          <span className="text-xs font-medium text-white/70 uppercase tracking-widest">Click to flip back</span>
        </div>
      </motion.div>
    </motion.div>
  );
}

function FlipCards() {
  // index of the card that is currently flipped open, or null if none is open
  const [activeIndex, setActiveIndex] = useState(null);
  const timerRef = useRef(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // schedules the next step of the auto cycle starting from `fromIndex`
  const scheduleNext = (fromIndex) => {
    clearTimer();
    timerRef.current = setTimeout(() => {
      // close the current card
      setActiveIndex(null);
      timerRef.current = setTimeout(() => {
        // open the next one
        const next = (fromIndex + 1) % cards.length;
        setActiveIndex(next);
        scheduleNext(next);
      }, GAP_MS);
    }, OPEN_MS);
  };

  useEffect(() => {
    // kick off the auto cycle on mount, starting with the first card
    timerRef.current = setTimeout(() => {
      setActiveIndex(0);
      scheduleNext(0);
    }, GAP_MS);

    return clearTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCardClick = (i) => {
    clearTimer();
    setActiveIndex((prev) => {
      // clicking the already-open card just closes it; clicking any other opens it
      const next = prev === i ? null : i;
      // resume the auto cycle from wherever the user left it
      scheduleNext(next === null ? i : next);
      return next;
    });
  };

  return (
    <section className="w-full bg-white py-12 sm:py-16 lg:py-20">
      <div className="w-full">
        <div className="text-center mb-10">
          <h2 className="text-[clamp(28px,3.5vw,52px)] font-bold text-[#1a1a1a]">
            Our <span className="text-[#c021d6]">Services</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8">
          {cards.map((c, i) => (
            <FlipCard
              key={c.title}
              {...c}
              flipped={activeIndex === i}
              onClick={() => handleCardClick(i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export default FlipCards;